import { FinancialHealthStats } from '../types/financialStats';

function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) return 'N/A';
  return num.toFixed(4);
}

function formatPercentage(num: number | null | undefined): string {
  if (num === null || num === undefined) return 'N/A';
  return (num * 100).toFixed(2) + '%';
}

function calculateDifference(a: number | null | undefined, b: number | null | undefined): { absolute: number; percentage: number } {
  if (a === null || a === undefined || b === null || b === undefined) {
    return { absolute: 0, percentage: 0 };
  }
  const absolute = b - a;
  const percentage = a !== 0 ? (absolute / Math.abs(a)) : 0;
  return { absolute, percentage };
}

export function compareVeraScores(baseline: FinancialHealthStats, calculated: FinancialHealthStats): string {
  let output = '';

  // Overall VeraScore
  output += '\n=== VeraScore Comparison ===\n';
  output += '┌──────────┬──────────┬──────────┬──────────┐\n';
  output += '│ Metric   │ UI       │ Calc     │ Diff     │\n';
  output += '├──────────┼──────────┼──────────┼──────────┤\n';
  const scoreDiff = calculateDifference(baseline.verascore, calculated.verascore);
  output += `│ Score    │ ${formatNumber(baseline.verascore).padEnd(8)} │ ${formatNumber(calculated.verascore).padEnd(8)} │ ${formatPercentage(scoreDiff.percentage).padEnd(8)} │\n`;
  output += '└──────────┴──────────┴──────────┴──────────┘\n\n';

  // DTI Factor
  output += '=== Factor Details ===\n';
  output += '┌──────────────┬─────────┬──────────┬──────────┬──────────┐\n';
  output += '│ Factor       │ Type    │ Ratio    │ Score    │ Health   │\n';
  output += '├──────────────┼─────────┼──────────┼──────────┼──────────┤\n';

  // DTI
  output += '│ DTI         │ UI      │ ' + formatNumber(baseline.dti_ratio).padEnd(8) + ' │ ' + formatNumber(baseline.factor_dti).padEnd(8) + ' │ ' + (baseline.dti_health || 'N/A').padEnd(8) + ' │\n';
  output += '│             │ Calc    │ ' + formatNumber(calculated.dti_ratio).padEnd(8) + ' │ ' + formatNumber(calculated.factor_dti).padEnd(8) + ' │ ' + (calculated.dti_health || 'N/A').padEnd(8) + ' │\n';
  output += '├──────────────┼─────────┼──────────┼──────────┼──────────┤\n';

  // Cash on Hand
  output += 'Cash on Hand:\n';
  output += `  Baseline: Ratio=${formatNumber(baseline.cash_lr_ratio)}, Factor=${formatNumber(baseline.factor_cash_on_hand)}, Health=${baseline.cash_lr_health}\n`;
  output += `  Calculated: Ratio=${formatNumber(calculated.cash_lr_ratio)}, Factor=${formatNumber(calculated.factor_cash_on_hand)}, Health=${calculated.cash_lr_health}\n`;
  output += `  Raw Values: Liquid Reserves=${formatNumber(calculated.liquid_reserves)}, Monthly Expenses=${formatNumber(calculated.monthly_expenses)}\n\n`;

  // Spending
  output += 'Spending:\n';
  output += `  Baseline: Ratio=${formatNumber(baseline.spending_cf_ratio)}, Factor=${formatNumber(baseline.factor_spending)}, Health=${baseline.spending_cf_health}\n`;
  output += `  Calculated: Ratio=${formatNumber(calculated.spending_cf_ratio)}, Factor=${formatNumber(calculated.factor_spending)}, Health=${calculated.spending_cf_health}\n`;
  output += `  Raw Values: Money In=${formatNumber(calculated.money_in)}, Monthly Expenses=${formatNumber(calculated.monthly_expenses)}\n\n`;

  // Savings
  output += 'Savings:\n';
  output += `  Baseline: Ratio=${formatNumber(baseline.savings_s_ratio)}, Factor=${formatNumber(baseline.factor_savings)}, Health=${baseline.savings_s_health}\n`;
  output += `  Calculated: Ratio=${formatNumber(calculated.savings_s_ratio)}, Factor=${formatNumber(calculated.factor_savings)}, Health=${calculated.savings_s_health}\n`;
  output += `  Raw Values: Net Savings=${formatNumber(calculated.net_savings)}, Income=${formatNumber(calculated.gross_income)}\n\n`;

  // Debt
  output += 'Debt:\n';
  output += `  Baseline: Ratio=${formatNumber(baseline.debt_ratio)}, Factor=${formatNumber(baseline.factor_debt)}, Health=${baseline.debt_health}\n`;
  output += `  Calculated: Ratio=${formatNumber(calculated.debt_ratio)}, Factor=${formatNumber(calculated.factor_debt)}, Health=${calculated.debt_health}\n`;
  output += `  Raw Values: Outstanding Debt=${formatNumber(calculated.total_outstanding_adverse_debt)}, Actual Payments=${formatNumber(calculated.actual_adverse_debt_payments)}\n\n`;

  // Payment History
  output += 'Payment History:\n';
  output += `  Baseline: Factor=${formatNumber(baseline.factor_payment_history)}, Health=${baseline.payment_history_health}\n`;
  output += `  Calculated: Factor=${formatNumber(calculated.factor_payment_history)}, Health=${calculated.payment_history_health}\n`;
  output += `  Raw Values: Late Payments=${formatNumber(calculated.number_of_late_payments)}, Age of Most Recent=${formatNumber(calculated.age_most_recent_late)}\n\n`;

  // Raw Values
  output += '=== Raw Values ===\n';
  output += '┌────────────────────────┬──────────────┬──────────────┐\n';
  output += '│ Metric                 │ UI           │ Calculated   │\n';
  output += '├────────────────────────┼──────────────┼──────────────┤\n';
  output += `│ Gross Income           │ ${formatNumber(baseline.gross_income).padEnd(12)} │ ${formatNumber(calculated.gross_income).padEnd(12)} │\n`;
  output += `│ Min Debt Payments      │ ${formatNumber(baseline.minimum_debt_payments).padEnd(12)} │ ${formatNumber(calculated.minimum_debt_payments).padEnd(12)} │\n`;
  output += `│ Liquid Reserves        │ ${formatNumber(baseline.liquid_reserves).padEnd(12)} │ ${formatNumber(calculated.liquid_reserves).padEnd(12)} │\n`;
  output += `│ Monthly Expenses       │ ${formatNumber(baseline.monthly_expenses).padEnd(12)} │ ${formatNumber(calculated.monthly_expenses).padEnd(12)} │\n`;
  output += `│ Money In               │ ${formatNumber(baseline.money_in).padEnd(12)} │ ${formatNumber(calculated.money_in).padEnd(12)} │\n`;
  output += `│ Net Savings            │ ${formatNumber(baseline.net_savings).padEnd(12)} │ ${formatNumber(calculated.net_savings).padEnd(12)} │\n`;
  output += `│ Outstanding Debt       │ ${formatNumber(baseline.total_outstanding_adverse_debt).padEnd(12)} │ ${formatNumber(calculated.total_outstanding_adverse_debt).padEnd(12)} │\n`;
  output += `│ Actual Debt Payments   │ ${formatNumber(baseline.actual_adverse_debt_payments).padEnd(12)} │ ${formatNumber(calculated.actual_adverse_debt_payments).padEnd(12)} │\n`;
  output += `│ Late Payments          │ ${formatNumber(baseline.number_of_late_payments).padEnd(12)} │ ${formatNumber(calculated.number_of_late_payments).padEnd(12)} │\n`;
  output += `│ Age Most Recent Late   │ ${formatNumber(baseline.age_most_recent_late).padEnd(12)} │ ${formatNumber(calculated.age_most_recent_late).padEnd(12)} │\n`;
  output += '└────────────────────────┴──────────────┴──────────────┘\n';

  return output;
} 